/**
 * AdminPolicyCompliancePage — full employee × policy compliance matrix.
 *
 * Renders a flat table (one row per employee-document combo) with:
 *   - Employee (name + email + code)
 *   - Document (title + kind chip)
 *   - Current Version vs Accepted Version (+ mismatch highlight)
 *   - Accepted Date
 *   - Status: Up to Date / Pending
 *
 * Toolbar has:
 *   - Search box (employee/document name)
 *   - Kind filter (all / policy / procedure)
 *   - Status filter (all / up_to_date / pending)
 *   - "Export Excel" button that streams the server-side .xlsx
 *
 * Backed by:
 *   GET  /api/admin/compliance/policies?kind=&status=&q=
 *   GET  /api/admin/compliance/policies/export.xlsx?...
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, AlertCircle, Download, Search, RefreshCw } from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'dd MMM yyyy'); }
  catch { return iso; }
};

export default function AdminPolicyCompliancePage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (status !== 'all') params.status = status;
      if (q.trim()) params.q = q.trim();
      const { data: d } = await axios.get(`${API}/admin/compliance/policies`, { params });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load compliance');
    } finally {
      setLoading(false);
    }
  }, [kind, status, q]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (kind !== 'all') params.kind = kind;
      if (status !== 'all') params.status = status;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${API}/admin/compliance/policies/export.xlsx`, {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `policy_compliance_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const rows = data?.rows || [];
  const s = data?.summary || { total_rows: 0, up_to_date: 0, pending: 0, employees: 0, documents: 0 };

  const kpis = useMemo(() => ([
    { label: 'Total records', value: s.total_rows, testId: 'kpi-total' },
    { label: 'Up to date', value: s.up_to_date, tone: 'emerald', testId: 'kpi-up-to-date' },
    { label: 'Pending', value: s.pending, tone: 'amber', testId: 'kpi-pending' },
    { label: 'Employees', value: s.employees, testId: 'kpi-employees' },
    { label: 'Documents', value: s.documents, testId: 'kpi-documents' },
  ]), [s]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto space-y-4">
        <div className="border-b border-border pb-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold uppercase tracking-wider" data-testid="page-title">
              Policy Compliance
            </h1>
            <p className="text-xs text-muted-foreground mt-1">
              Employee × Policy matrix — spot who is on an outdated version and who never acknowledged.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="rounded-sm border-border uppercase tracking-wider text-xs h-8"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={handleExport}
              disabled={exporting || loading}
              className="rounded-sm bg-primary hover:bg-primary/90 uppercase tracking-wider text-xs h-8"
              data-testid="export-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              {exporting ? 'Exporting…' : 'Export Excel'}
            </Button>
          </div>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {kpis.map((k) => (
            <Card key={k.label} className="bg-card border-border rounded-sm">
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k.label}</p>
                <p
                  data-testid={k.testId}
                  className={
                    'text-2xl font-semibold mt-1 ' +
                    (k.tone === 'emerald' ? 'text-emerald-500' :
                     k.tone === 'amber' ? 'text-amber-500' : 'text-foreground')
                  }
                >
                  {k.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="flex-1 relative">
              <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search employee name, email, or document…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 rounded-sm border-border h-9"
                data-testid="search-input"
              />
            </div>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-full sm:w-40 rounded-sm border-border h-9" data-testid="kind-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                <SelectItem value="policy">Policies</SelectItem>
                <SelectItem value="procedure">Procedures</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-full sm:w-40 rounded-sm border-border h-9" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="up_to_date">Up to Date</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Matrix table */}
        <Card className="bg-card border-border rounded-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="compliance-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Employee</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Document</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Current</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Accepted</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Accepted On</th>
                    <th className="text-left px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground italic">Loading…</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground italic" data-testid="empty-state">No records match the current filters.</td></tr>
                  ) : rows.map((r, i) => {
                    const isPending = r.status === 'pending';
                    return (
                      <tr
                        key={`${r.employee_id}-${r.document_id}`}
                        className="border-b border-border/50 hover:bg-accent/30 transition-colors"
                        data-testid={`compliance-row-${i}`}
                      >
                        <td className="px-4 py-2">
                          <div className="flex flex-col">
                            <span className="text-foreground font-medium">{r.employee_name || '—'}</span>
                            <span className="text-[11px] text-muted-foreground">
                              {r.employee_email}
                              {r.employee_code ? ` · #${r.employee_code}` : ''}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            <Badge className={
                              r.kind === 'policy'
                                ? 'bg-blue-500/15 text-blue-500 border-blue-500/40 text-[9px] uppercase tracking-wider'
                                : 'bg-purple-500/15 text-purple-500 border-purple-500/40 text-[9px] uppercase tracking-wider'
                            }>
                              {r.kind}
                            </Badge>
                            <span className="text-foreground truncate max-w-[280px]" title={r.title}>{r.title}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-foreground font-mono text-xs">v{r.current_version}</td>
                        <td className={`px-4 py-2 font-mono text-xs ${isPending ? 'text-muted-foreground' : (r.accepted_version < r.current_version ? 'text-amber-500' : 'text-foreground')}`}>
                          {r.accepted_version ? `v${r.accepted_version}` : '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{fmt(r.accepted_at)}</td>
                        <td className="px-4 py-2">
                          {isPending ? (
                            <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/40 text-[10px] uppercase tracking-wider" data-testid="status-pending">
                              <AlertCircle className="w-3 h-3 mr-1" /> Pending
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px] uppercase tracking-wider" data-testid="status-up-to-date">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Up to Date
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
